import { useCallback, useEffect, useState } from 'react';
import type {
  EditarIncidencia,
  Adjunto,
  CanalComentario,
  ComentarioIncidencia,
  Incidencia,
  PresupuestoIncidencia,
  Proveedor,
  ResponsablePosible,
  UsuarioSesion,
} from '@matias-asset-os/contracts';
import { formatearFechaHora, formatearImporteDesdeCents, t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { Modal } from '../componentes/Modal.js';
import { NuevaIncidencia } from '../componentes/NuevaIncidencia.js';
import { Icono } from '../componentes/Iconos.js';

/** Abierta y en curso pesan lo mismo; el servidor opina igual. */
const incidenciaActiva = (estado: string) => estado === 'OPEN' || estado === 'IN_PROGRESS';

interface Props {
  incidenciaId: string;
  propiedadId: string;
  usuario: UsuarioSesion;
  idioma: Idioma;
  onVolver: () => void;
}

const NIVELES = [1, 2, 3, 4] as const;

/**
 * Detalle de una incidencia: lo que se mira y lo que se hace con ella.
 *
 * Los dos hilos de conversación están separados y se dice cuál es cuál. El
 * interno no se pide siquiera cuando quien mira no es del equipo: el servidor
 * lo rechazaría, y pedirlo igualmente sería fingir que existe.
 */
export function IncidenciaDetalle({ incidenciaId, propiedadId, usuario, idioma, onVolver }: Props) {
  const [incidencia, setIncidencia] = useState<Incidencia | null>(null);
  const [presupuestos, setPresupuestos] = useState<PresupuestoIncidencia[]>([]);
  const [adjuntos, setAdjuntos] = useState<Adjunto[]>([]);
  const [internos, setInternos] = useState<ComentarioIncidencia[]>([]);
  const [cliente, setCliente] = useState<ComentarioIncidencia[]>([]);
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [responsables, setResponsables] = useState<ResponsablePosible[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [cambiandoEstado, setCambiandoEstado] = useState(false);
  const [creando, setCreando] = useState(false);
  /* El titulo y la descripcion se corrigen donde se leen. */
  const [editandoTexto, setEditandoTexto] = useState(false);
  const [guardandoTexto, setGuardandoTexto] = useState(false);
  const [errorTexto, setErrorTexto] = useState<string | null>(null);
  const [borrador, setBorrador] = useState({ titulo: '', descripcion: '' });

  async function guardar(datos: EditarIncidencia): Promise<void> {
    setGuardandoTexto(true);
    setErrorTexto(null);
    try {
      await api.editarIncidencia(incidenciaId, datos);
      setEditandoTexto(false);
      recargar();
    } catch (e) {
      setErrorTexto(e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'error.servidor'));
    } finally {
      setGuardandoTexto(false);
    }
  }

  const esInterno = usuario.rol === 'DIRECTOR' || usuario.rol === 'MANAGER';

  const cargar = useCallback(async () => {
    const opcional = async <T,>(p: Promise<T>, def: T): Promise<T> => {
      try {
        return await p;
      } catch (f: unknown) {
        if (f instanceof ErrorApi && (f.estado === 403 || f.estado === 404)) return def;
        throw f;
      }
    };
    const [inc, pre, adj, int, cli, prov, resp] = await Promise.all([
      api.incidencias(propiedadId),
      esInterno ? opcional(api.presupuestos(incidenciaId), { presupuestos: [] }) : { presupuestos: [] },
      opcional(api.adjuntos('INCIDENCIA', incidenciaId), { adjuntos: [] }),
      esInterno
        ? opcional(api.comentarios(incidenciaId, 'INTERNO'), { comentarios: [] })
        : { comentarios: [] },
      opcional(api.comentarios(incidenciaId, 'CLIENTE'), { comentarios: [] }),
      esInterno ? opcional(api.proveedores(), { proveedores: [] }) : { proveedores: [] },
      esInterno
        ? opcional(api.responsablesPosibles(propiedadId), { responsables: [] })
        : { responsables: [] },
    ]);
    setIncidencia(inc.incidencias.find((i) => i.id === incidenciaId) ?? null);
    setPresupuestos(pre.presupuestos);
    setAdjuntos(adj.adjuntos);
    setInternos(int.comentarios);
    setCliente(cli.comentarios);
    setProveedores(prov.proveedores);
    setResponsables(resp.responsables);
  }, [incidenciaId, propiedadId, esInterno]);

  useEffect(() => {
    setCargando(true);
    cargar()
      .catch(() => setError(t(idioma, 'error.servidor')))
      .finally(() => setCargando(false));
  }, [cargar, idioma]);

  const recargar = () => void cargar().catch(() => setError(t(idioma, 'error.servidor')));
  /** Abierta ↔ En curso. No pide justificacion: empezar no afirma nada. */
  const cambiarEstado = async (enCurso: boolean) => {
    setCambiandoEstado(true);
    setError(null);
    try {
      await api.incidenciaEnCurso(incidenciaId, enCurso);
      await recargar();
    } catch (f: unknown) {
      setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor'));
    } finally {
      setCambiandoEstado(false);
    }
  };

  /*
   * Cerrar SI pide texto, y por eso no es un interruptor mas: cerrar afirma que
   * el problema ya no esta, y esa afirmacion tiene que quedar escrita y firmada
   * por alguien. El servidor tampoco la acepta sin resolucion.
   */
  const cerrar = async () => {
    const resolucion = window.prompt(t(idioma, 'inc.pedirResolucion'));
    if (resolucion === null || resolucion.trim().length < 3) return;
    setCambiandoEstado(true);
    setError(null);
    try {
      await api.cerrarIncidencia(incidenciaId, resolucion.trim());
      await recargar();
    } catch (f: unknown) {
      setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor'));
    } finally {
      setCambiandoEstado(false);
    }
  };

  const dinero = (c: number) => formatearImporteDesdeCents(c, idioma, { moneda: 'EUR' });

  const actualizar = (datos: Parameters<typeof api.actualizarIncidencia>[1]) => {
    setError(null);
    api
      .actualizarIncidencia(incidenciaId, datos)
      .then(recargar)
      .catch((e: unknown) =>
        setError(e instanceof ErrorApi && e.message ? e.message : t(idioma, 'error.servidor')),
      );
  };

  const subir = (fichero: File) => {
    setSubiendo(true);
    api
      .subirAdjunto('INCIDENCIA', incidenciaId, fichero)
      .then(recargar)
      .catch((f: unknown) =>
        setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor')),
      )
      .finally(() => setSubiendo(false));
  };

  if (cargando) return <p>{t(idioma, 'app.cargando')}</p>;
  if (!incidencia)
    return (
      <main>
        <p className="error" role="alert">
          {error ?? t(idioma, 'error.noEncontrado')}
        </p>
        <button type="button" className="secundario" onClick={onVolver}>
          {t(idioma, 'app.volver')}
        </button>
      </main>
    );

  const fotos = adjuntos.filter((a) => a.tipoMime.startsWith('image/'));

  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'ficha.incidencias')}</h1>
          <p className="numerico">{incidencia.codigo}</p>
        </div>
        <div className="acciones">
          {/*
            Dar de alta otra incidencia desde aqui, que es donde aparece la
            siguiente: mirando una gotera se ve la mancha de al lado, y volver
            a la casa para apuntarla es la forma segura de no apuntarla.
          */}
          {esInterno && (
            <button type="button" onClick={() => setCreando(true)}>
              + {t(idioma, 'incidencia.agregar')}
            </button>
          )}
          <button type="button" className="secundario" onClick={onVolver}>
            ‹ {t(idioma, 'app.volver')}
          </button>
        </div>
      </div>

      {creando && (
        <Modal
          idioma={idioma}
          titulo={t(idioma, 'incidencia.agregar')}
          icono="aviso"
          onCerrar={() => setCreando(false)}
        >
          <NuevaIncidencia
            idioma={idioma}
            propiedadId={propiedadId}
            onCreada={() => {
              setCreando(false);
              void recargar();
            }}
            onCancelar={() => setCreando(false)}
          />
        </Modal>
      )}

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {/*
        --- Responsable del equipo ------------------------------------------
        QUIEN SE ENCARGA. El proveedor la arregla; el responsable la persigue,
        llama al herrero y la cierra. Sin esto una incidencia abierta es de
        todos, que es lo mismo que de nadie. Le sale en su «Tareas del dia».
        Quien no tiene la casa asignada aparece, pero no se puede elegir: si no
        apareciera, nadie sabria por que falta.
      */}
      {esInterno && (
        <section className="tarjeta">
          <div className="tarjeta-cabeza">
            <h2>{t(idioma, 'inc.responsable')}</h2>
            <span className="suave">
              {incidencia.responsableNombre ?? t(idioma, 'inc.sinResponsable')}
            </span>
          </div>
          <div className="fila-flex" style={{ gap: '0.5rem' }}>
            <select
              aria-label={t(idioma, 'inc.responsable')}
              value={incidencia.responsableId ?? ''}
              onChange={(e) =>
                actualizar({ responsableId: e.target.value === '' ? null : e.target.value })
              }
              style={{ flex: 1 }}
            >
              <option value="">{t(idioma, 'inc.elegirResponsable')}</option>
              {responsables.map((r) => (
                <option key={r.id} value={r.id} disabled={!r.conAcceso}>
                  {r.nombre}
                  {r.conAcceso ? '' : ` · ${t(idioma, 'inc.sinAccesoCasa')}`}
                </option>
              ))}
            </select>
            {incidencia.responsableId && (
              <button
                type="button"
                className="secundario"
                onClick={() => actualizar({ responsableId: null })}
              >
                {t(idioma, 'inc.quitar')}
              </button>
            )}
          </div>
          <p className="suave pequeno">{t(idioma, 'inc.responsableNota')}</p>
        </section>
      )}

      {/* --- Proveedor asignado --------------------------------------------- */}
      {esInterno && (
        <section className="tarjeta">
          <div className="tarjeta-cabeza">
            <h2>{t(idioma, 'inc.proveedor')}</h2>
            <span className="suave">
              {incidencia.proveedorNombre ?? t(idioma, 'inc.sinAsignar')}
            </span>
          </div>
          <div className="fila-flex" style={{ gap: '0.5rem' }}>
            <select
              aria-label={t(idioma, 'inc.proveedor')}
              value={''}
              onChange={(e) => e.target.value && actualizar({ providerId: e.target.value })}
              style={{ flex: 1 }}
            >
              <option value="">{t(idioma, 'inc.elegirProveedor')}</option>
              {proveedores
                .filter((p) => p.activo)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre} · {t(idioma, `oficio.${p.oficio}`)}
                  </option>
                ))}
            </select>
            {incidencia.proveedorNombre && (
              <button type="button" className="secundario" onClick={() => actualizar({ providerId: null })}>
                {t(idioma, 'inc.quitar')}
              </button>
            )}
          </div>
        </section>
      )}

      {/* --- Presupuestos --------------------------------------------------- */}
      {esInterno && (
        <PanelPresupuestos
          idioma={idioma}
          incidenciaId={incidenciaId}
          presupuestos={presupuestos}
          proveedores={proveedores}
          dinero={dinero}
          onCambio={recargar}
        />
      )}

      {/* --- Urgencia y datos ----------------------------------------------- */}
      <section className="tarjeta">
        <div className="tarjeta-cabeza">
          <h3>{t(idioma, 'inc.urgencia')}</h3>
          {/*
            Abierta · En curso · Cerrada, a la vista. «En curso» sigue contando
            como activa: la casa no se pone en verde porque alguien haya
            empezado a mirar el problema.

            Cerrar no esta aqui dentro sino como accion aparte: exige escribir
            la resolucion, y un boton que pide un texto no es un interruptor.
          */}
          {esInterno && incidenciaActiva(incidencia.estado) ? (
            <div
              className="cobro-estados"
              role="group"
              aria-label={t(idioma, 'inc.estado')}
            >
              <button
                type="button"
                className="cobro-boton"
                aria-pressed={incidencia.estado === 'OPEN'}
                disabled={cambiandoEstado || incidencia.estado === 'OPEN'}
                onClick={() => void cambiarEstado(false)}
              >
                {t(idioma, 'estadoIncidencia.OPEN')}
              </button>
              <button
                type="button"
                className="cobro-boton"
                aria-pressed={incidencia.estado === 'IN_PROGRESS'}
                disabled={cambiandoEstado || incidencia.estado === 'IN_PROGRESS'}
                onClick={() => void cambiarEstado(true)}
              >
                {t(idioma, 'estadoIncidencia.IN_PROGRESS')}
              </button>
              <button
                type="button"
                className="cobro-boton"
                aria-pressed={false}
                disabled={cambiandoEstado}
                onClick={() => void cerrar()}
              >
                {t(idioma, 'estadoIncidencia.CLOSED')}
              </button>
            </div>
          ) : (
            <span
              className={`pastilla ${
                incidencia.estado === 'CLOSED'
                  ? 'verde'
                  : incidencia.estado === 'CANCELLED'
                    ? 'neutra'
                    : 'ambar'
              }`}
            >
              {t(idioma, `estadoIncidencia.${incidencia.estado}`)}
            </span>
          )}
        </div>

        {/*
          Los cuatro niveles estaban pintados y apagados: se veian, no hacian
          nada y nadie sabia por que. El servidor siempre los dejo cambiar.
        */}
        <div className="conmutador-vista" role="group" aria-label={t(idioma, 'inc.urgencia')}>
          {NIVELES.map((n) => (
            <button
              key={n}
              type="button"
              aria-pressed={incidencia.nivel === n}
              disabled={!esInterno || guardandoTexto || incidencia.nivel === n}
              onClick={() => void guardar({ nivel: n })}
            >
              N{n}
            </button>
          ))}
        </div>

        <div className="fila-flex" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
          <span className="chip-area">{t(idioma, `areaMant.${incidencia.area}`)}</span>
          {incidencia.slaIncumplido && (
            <span className="chip-sla">
              <Icono nombre="cronometro" /> {t(idioma, 'ficha.slaIncumplido')} {incidencia.horasTranscurridas}h/
              {incidencia.slaHoras}h
            </span>
          )}
        </div>

        <p className="numerico suave" style={{ fontSize: '0.8125rem' }}>
          {incidencia.codigo}
        </p>

        {/*
          EL TITULO SE CORRIGE DONDE SE LEE.

          Lo escribe quien abre la incidencia, muchas veces desde la casa y con
          prisa, y se queda mal: «Solucionar fallo electrico» cuando lo que pasa
          es que la vela del salon abre y cierra sola. Mandar a alguien a la
          oficina a corregirlo garantiza que no se corrige.
        */}
        {editandoTexto ? (
          <form
            className="formulario editar-texto"
            style={{ maxWidth: 'none' }}
            onSubmit={(e) => {
              e.preventDefault();
              void guardar({ titulo: borrador.titulo.trim(), descripcion: borrador.descripcion });
            }}
          >
            <div className="campo">
              <label htmlFor="inc-titulo">{t(idioma, 'inc.titulo')}</label>
              <input
                id="inc-titulo"
                type="text"
                value={borrador.titulo}
                maxLength={300}
                required
                minLength={3}
                onChange={(e) => setBorrador((b) => ({ ...b, titulo: e.target.value }))}
              />
            </div>
            <div className="campo">
              <label htmlFor="inc-desc">{t(idioma, 'inc.descripcion')}</label>
              <textarea
                id="inc-desc"
                rows={3}
                maxLength={4000}
                value={borrador.descripcion}
                onChange={(e) => setBorrador((b) => ({ ...b, descripcion: e.target.value }))}
              />
            </div>
            <div className="fila-flex" style={{ gap: '0.5rem' }}>
              <button type="submit" disabled={guardandoTexto || borrador.titulo.trim().length < 3}>
                {t(idioma, 'app.guardar')}
              </button>
              <button
                type="button"
                className="secundario"
                disabled={guardandoTexto}
                onClick={() => setEditandoTexto(false)}
              >
                {t(idioma, 'app.cancelar')}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="titulo-incidencia">
              <h2>{incidencia.titulo}</h2>
              {esInterno && (
                <button
                  type="button"
                  className="enlace"
                  onClick={() => {
                    setBorrador({
                      titulo: incidencia.titulo,
                      descripcion: incidencia.descripcion ?? '',
                    });
                    setEditandoTexto(true);
                  }}
                >
                  {t(idioma, 'app.editar')}
                </button>
              )}
            </div>
            {incidencia.descripcion && <p className="suave">{incidencia.descripcion}</p>}
          </>
        )}
        {errorTexto && (
          <p className="error" role="alert">
            {errorTexto}
          </p>
        )}
      </section>

      {(incidencia.costeEstimadoCents !== null || incidencia.costeFinalCents !== null) && (
        <div className="rejilla kpis">
          {incidencia.costeEstimadoCents !== null && (
            <div className="tarjeta kpi-card">
              <h3>{t(idioma, 'inc.costeEstimado')}</h3>
              <span className="kpi-valor">{dinero(incidencia.costeEstimadoCents)}</span>
            </div>
          )}
          {incidencia.costeFinalCents !== null && (
            <div className="tarjeta kpi-card">
              <h3>{t(idioma, 'inc.costeFinal')}</h3>
              <span className="kpi-valor">{dinero(incidencia.costeFinalCents)}</span>
            </div>
          )}
        </div>
      )}

      {/* --- Fotos de evidencia --------------------------------------------- */}
      <section className="tarjeta">
        <div className="tarjeta-cabeza">
          <h2>
            {t(idioma, 'inc.fotos')} ({fotos.length})
          </h2>
          {esInterno && (
            <label className="enlace" style={{ cursor: 'pointer' }}>
              + {t(idioma, 'inc.anadirFoto')}
              <input
                type="file"
                accept="image/*,application/pdf"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) subir(f);
                  e.target.value = '';
                }}
              />
            </label>
          )}
        </div>
        {subiendo && <p className="suave">{t(idioma, 'app.cargando')}</p>}
        {adjuntos.length === 0 && !subiendo && (
          <p className="suave">{t(idioma, 'inc.sinFotos')}</p>
        )}
        {adjuntos.length > 0 && (
          <div className="rejilla tarjetas">
            {adjuntos.map((a) => (
              <figure key={a.id} className="evidencia">
                <Vista idioma={idioma} adjunto={a} />
                {/*
                  Quien decide si el propietario ve una evidencia es el equipo.
                  El boton dice el estado ACTUAL y lo que hara al pulsarlo, para
                  que nadie comparta una foto creyendo que la esta ocultando.
                */}
                {esInterno && (
                  <button
                    type="button"
                    className={`evidencia-ojo ${a.visibleCliente ? 'compartida' : ''}`}
                    aria-pressed={a.visibleCliente}
                    title={t(
                      idioma,
                      a.visibleCliente ? 'inc.fotoVisibleAyuda' : 'inc.fotoOcultaAyuda',
                    )}
                    onClick={() =>
                      void api
                        .visibilidadAdjunto(a.id, !a.visibleCliente)
                        .then(recargar)
                        .catch(() => undefined)
                    }
                  >
                    {a.visibleCliente ? '👁' : '🚫'}
                    <span className="evidencia-ayuda">
                      {t(idioma, a.visibleCliente ? 'inc.fotoVisibleAyuda' : 'inc.fotoOcultaAyuda')}
                    </span>
                  </button>
                )}
                {esInterno && (
                  <button
                    type="button"
                    className="enlace evidencia-quitar"
                    onClick={() => void api.eliminarAdjunto(a.id).then(recargar)}
                  >
                    {t(idioma, 'checklists.eliminar')}
                  </button>
                )}
              </figure>
            ))}
          </div>
        )}
      </section>

      {/* --- Conversación ---------------------------------------------------- */}
      {esInterno && (
        <Hilo
          idioma={idioma}
          titulo={t(idioma, 'inc.chatInterno')}
          nota={t(idioma, 'inc.chatInternoNota')}
          canal="INTERNO"
          comentarios={internos}
          incidenciaId={incidenciaId}
          onCambio={recargar}
        />
      )}

      <Hilo
        idioma={idioma}
        titulo={t(idioma, 'inc.mensajesCliente')}
        nota={t(idioma, 'inc.mensajesClienteNota')}
        canal="CLIENTE"
        comentarios={cliente}
        incidenciaId={incidenciaId}
        onCambio={recargar}
        visibleCliente
      />

      {/* --- Fecha esperada de resolucion ------------------------------------- */}
      {/*
        NO ES EL SLA. El SLA dice cuantas horas deberia tardar segun su nivel y
        sirve para medir el servicio; esta es la fecha que el equipo se pone, y
        sirve para planificar. El dia que llega —o el dia que pasa sin que este
        cerrada— sale en «Tareas del dia», igual que una visita.

        Se deja vaciar: dejar de prometer una fecha es una decision valida, y
        una fecha vieja que nadie va a cumplir estorba mas que no tener ninguna.
      */}
      {esInterno && (
        <section className="tarjeta">
          <h3>{t(idioma, 'inc.fechaEsperada')}</h3>
          <div className="campo">
            <label htmlFor="inc-fecha-esperada" className="oculto-visual">
              {t(idioma, 'inc.fechaEsperada')}
            </label>
            <input
              id="inc-fecha-esperada"
              type="date"
              value={incidencia.fechaEsperada ?? ''}
              onChange={(e) => actualizar({ fechaEsperada: e.target.value || null })}
            />
          </div>
          <p className="suave" style={{ fontSize: '0.8125rem' }}>
            {t(idioma, 'inc.fechaEsperadaNota')}
          </p>
        </section>
      )}

      {/* --- Visibilidad ------------------------------------------------------ */}
      {esInterno && (
        <section className="tarjeta">
          <h3>{t(idioma, 'inc.visibilidad')}</h3>
          <label className="conmutador-check">
            <input
              type="checkbox"
              checked={incidencia.visibleCliente}
              onChange={(e) => actualizar({ visibleCliente: e.target.checked })}
            />{' '}
            {incidencia.visibleCliente
              ? t(idioma, 'inc.visiblePropietario')
              : t(idioma, 'inc.ocultaPropietario')}
          </label>
          <p className="suave" style={{ fontSize: '0.8125rem' }}>
            {t(idioma, 'inc.visibilidadNota')}
          </p>
        </section>
      )}

      <section className="tarjeta">
        <div className="fila-flex" style={{ justifyContent: 'space-between' }}>
          <span className="suave">{t(idioma, 'inc.creada')}</span>
          <span className="numerico">
            {formatearFechaHora(new Date(incidencia.creadoEn), idioma)}
          </span>
        </div>
        {incidencia.cerradoEn && (
          <div className="fila-flex" style={{ justifyContent: 'space-between' }}>
            <span className="suave">{t(idioma, 'inc.cerrada')}</span>
            <span className="numerico">
              {formatearFechaHora(new Date(incidencia.cerradoEn), idioma)}
            </span>
          </div>
        )}
      </section>
    </main>
  );
}

function Hilo({
  idioma,
  titulo,
  nota,
  canal,
  comentarios,
  incidenciaId,
  onCambio,
  visibleCliente = false,
}: {
  idioma: Idioma;
  titulo: string;
  nota: string;
  canal: CanalComentario;
  comentarios: ComentarioIncidencia[];
  incidenciaId: string;
  onCambio: () => void;
  visibleCliente?: boolean;
}) {
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);

  const enviar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!texto.trim()) return;
    setEnviando(true);
    api
      .comentar(incidenciaId, { canal, cuerpo: texto.trim() })
      .then(() => {
        setTexto('');
        onCambio();
      })
      .catch(() => undefined)
      .finally(() => setEnviando(false));
  };

  return (
    <section className="tarjeta">
      <div className="tarjeta-cabeza">
        <h2>{titulo}</h2>
        {visibleCliente && <span className="pastilla info">{t(idioma, 'inc.visibleAlCliente')}</span>}
      </div>

      {comentarios.length === 0 ? (
        <p className="suave">{nota}</p>
      ) : (
        <div className="lista">
          {comentarios.map((c) => (
            <div key={c.id} className="fila">
              <span className="crecer">
                <b>{c.autorNombre ?? '—'}</b>
                <small>{c.cuerpo}</small>
              </span>
              <span className="pequeno suave numerico">
                {formatearFechaHora(new Date(c.creadoEn), idioma)}
              </span>
            </div>
          ))}
        </div>
      )}

      <form className="formulario" style={{ maxWidth: 'none' }} onSubmit={enviar}>
        <div className="campo">
          <label htmlFor={`msg-${canal}`}>{titulo}</label>
          <textarea
            id={`msg-${canal}`}
            rows={2}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            maxLength={4000}
          />
        </div>
        <button type="submit" disabled={enviando || !texto.trim()} style={{ alignSelf: 'flex-start' }}>
          <Icono nombre="enviar" /> {t(idioma, 'inc.enviar')}
        </button>
      </form>
    </section>
  );
}

function PanelPresupuestos({
  idioma,
  incidenciaId,
  presupuestos,
  proveedores,
  dinero,
  onCambio,
}: {
  idioma: Idioma;
  incidenciaId: string;
  presupuestos: PresupuestoIncidencia[];
  proveedores: Proveedor[];
  dinero: (c: number) => string;
  onCambio: () => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [providerId, setProviderId] = useState('');
  const [importe, setImporte] = useState('');

  return (
    <section className="tarjeta">
      <div className="tarjeta-cabeza">
        <h2>{t(idioma, 'inc.presupuestos')}</h2>
        <button type="button" className="enlace" onClick={() => setAbierto((v) => !v)}>
          <Icono nombre="enviar" /> {t(idioma, 'inc.solicitar')}
        </button>
      </div>

      {presupuestos.length === 0 ? (
        <p className="suave">{t(idioma, 'inc.sinPresupuestos')}</p>
      ) : (
        <div className="lista">
          {presupuestos.map((p) => (
            <div key={p.id} className="fila">
              <span className="crecer">
                <b>{p.proveedorNombre ?? '—'}</b>
                {p.notas && <small>{p.notas}</small>}
              </span>
              <span className="numerico">{dinero(p.importeCents)}</span>
              <span className="chip-area">{p.estado}</span>
            </div>
          ))}
        </div>
      )}

      {abierto && (
        <form
          className="formulario"
          onSubmit={(e) => {
            e.preventDefault();
            const n = Number(importe.replace(',', '.'));
            if (!Number.isFinite(n) || n < 0) return;
            void api
              .registrarPresupuesto(incidenciaId, {
                importeCents: Math.round(n * 100),
                ...(providerId ? { providerId } : {}),
              })
              .then(() => {
                setImporte('');
                setProviderId('');
                setAbierto(false);
                onCambio();
              });
          }}
        >
          <div className="rejilla dos">
            <div className="campo">
              <label htmlFor="pre-prov">{t(idioma, 'inc.proveedor')}</label>
              <select id="pre-prov" value={providerId} onChange={(e) => setProviderId(e.target.value)}>
                <option value="">—</option>
                {proveedores.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div className="campo">
              <label htmlFor="pre-imp">{t(idioma, 'interno.importe')}</label>
              <input
                id="pre-imp"
                type="text"
                inputMode="decimal"
                value={importe}
                onChange={(e) => setImporte(e.target.value)}
              />
            </div>
          </div>
          <button type="submit" disabled={!importe}>
            {t(idioma, 'inc.guardarPresupuesto')}
          </button>
        </form>
      )}
    </section>
  );
}

/**
 * La evidencia, con su plan B.
 *
 * Un `<img>` que no carga deja un cuadro roto, y eso se lee como «la aplicacion
 * falla». Pasa por dos motivos reales: el fichero ya no esta en el almacen, o el
 * navegador no sabe pintar ese formato —las fotos HEIC del iPhone no se ven
 * fuera de Apple—. En los dos casos el fichero SIGUE ahi para descargarlo, asi
 * que se ofrece eso en vez de un hueco.
 */
function Vista({ idioma, adjunto }: { idioma: Idioma; adjunto: Adjunto }) {
  const [roto, setRoto] = useState(false);
  const url = api.urlAdjunto(adjunto.id);

  if (!adjunto.tipoMime.startsWith('image/') || roto) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="enlace evidencia-doc">
        <Icono nombre="documento" /> {adjunto.nombreFichero}
        {roto && <span className="suave"> · {t(idioma, 'inc.fotoNoSeVe')}</span>}
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img src={url} alt={adjunto.nombreFichero} onError={() => setRoto(true)} />
    </a>
  );
}
